import { lazy, Suspense, type ReactNode } from "react";
import type { ChatCardStage } from "@/shared/ui/chat-card/tool-call-store";
import {
  chatPrepareAnswered,
  hasChatPrepare,
  useChatPrepareStages,
} from "@/features/advice-portal/lib/chat-prepare";
import {
  decisionChainAnswered,
  hasDecisionChain,
  useDecisionChainStages,
} from "@/features/documents/lib/decision-chain-store";
import {
  annualFollowupAnswered,
  hasAnnualFollowup,
  useAnnualFollowupStages,
} from "@/features/clients/lib/annual-followup-store";
import {
  hasMeetingBrief,
  meetingBriefAnswered,
  useMeetingBriefStages,
} from "@/features/clients/lib/meeting-brief-store";
import {
  hasOrderBatch,
  orderBatchAnswered,
  useOrderBatchStages,
} from "@/features/order-instructions/lib/order-batch-store";
import {
  cashFlowAnswered,
  hasCashFlow,
  useCashFlowStages,
} from "@/features/portfolio-analysis/lib/cash-flow-store";
import {
  chatProposalAnswered,
  hasChatProposal,
  useChatProposalStages,
} from "@/features/portfolio-analysis/lib/chat-proposal-store";
import {
  hasProposalMail,
  proposalMailAnswered,
  useProposalMailStages,
} from "@/features/portfolio-analysis/lib/proposal-mail-store";
import {
  hasRiskExplanation,
  riskExplanationAnswered,
  useRiskExplanationStages,
} from "@/features/portfolio-analysis/lib/risk-explanation-store";
import type { ToolActivity } from "@/features/intelligence/lib/thread-state";

export interface ChatToolCards {
  stage: (tool: ToolActivity) => ChatCardStage | null;
  below: (tool: ToolActivity) => ReactNode;
}

export function useChatToolCards(): ChatToolCards {
  const prepareStage = useChatPrepareStages();
  const briefStage = useMeetingBriefStages();
  const ordersStage = useOrderBatchStages();
  const proposalStage = useChatProposalStages();
  const mailStage = useProposalMailStages();
  const riskStage = useRiskExplanationStages();
  const chainStage = useDecisionChainStages();
  const cashFlowStage = useCashFlowStages();
  const followupStage = useAnnualFollowupStages();
  return {
    stage: (tool) => {
      if (tool.toolName === "prepare_advisory") return prepareStage(tool.toolCallId);
      if (tool.toolName === "prepare_meeting_brief") return briefStage(tool.toolCallId);
      if (tool.toolName === "prepare_order_instructions") return ordersStage(tool.toolCallId);
      if (tool.toolName === "prepare_portfolio_proposal") return proposalStage(tool.toolCallId);
      if (tool.toolName === "prepare_proposal_mail") return mailStage(tool.toolCallId);
      if (tool.toolName === "prepare_risk_explanation") return riskStage(tool.toolCallId);
      if (tool.toolName === "prepare_decision_chain") return chainStage(tool.toolCallId);
      if (tool.toolName === "prepare_cash_flow") return cashFlowStage(tool.toolCallId);
      if (tool.toolName === "prepare_annual_followup") return followupStage(tool.toolCallId);
      return null;
    },
    below,
  };
}

export function chatToolCardAnswered(toolName: string, toolCallId: string): void {
  if (toolName === "prepare_advisory") chatPrepareAnswered(toolCallId);
  if (toolName === "prepare_meeting_brief") meetingBriefAnswered(toolCallId);
  if (toolName === "prepare_order_instructions") orderBatchAnswered(toolCallId);
  if (toolName === "prepare_portfolio_proposal") chatProposalAnswered(toolCallId);
  if (toolName === "prepare_proposal_mail") proposalMailAnswered(toolCallId);
  if (toolName === "prepare_risk_explanation") riskExplanationAnswered(toolCallId);
  if (toolName === "prepare_decision_chain") decisionChainAnswered(toolCallId);
  if (toolName === "prepare_cash_flow") cashFlowAnswered(toolCallId);
  if (toolName === "prepare_annual_followup") annualFollowupAnswered(toolCallId);
}

function below(tool: ToolActivity): ReactNode {
  if (tool.toolName === "prepare_advisory" && hasChatPrepare(tool.toolCallId)) {
    return (
      <Suspense fallback={null}>
        <ChatPrepareWidgets toolCallId={tool.toolCallId} />
      </Suspense>
    );
  }
  if (tool.toolName === "prepare_meeting_brief" && hasMeetingBrief(tool.toolCallId)) {
    return (
      <Suspense fallback={null}>
        <MeetingBriefCard toolCallId={tool.toolCallId} />
      </Suspense>
    );
  }
  if (tool.toolName === "prepare_order_instructions" && hasOrderBatch(tool.toolCallId)) {
    return (
      <Suspense fallback={null}>
        <OrderBatchCard toolCallId={tool.toolCallId} />
      </Suspense>
    );
  }
  if (tool.toolName === "prepare_portfolio_proposal" && hasChatProposal(tool.toolCallId)) {
    return (
      <Suspense fallback={null}>
        <ChatProposalCard toolCallId={tool.toolCallId} />
      </Suspense>
    );
  }
  if (tool.toolName === "prepare_proposal_mail" && hasProposalMail(tool.toolCallId)) {
    return (
      <Suspense fallback={null}>
        <ProposalMailCard toolCallId={tool.toolCallId} />
      </Suspense>
    );
  }
  if (tool.toolName === "prepare_risk_explanation" && hasRiskExplanation(tool.toolCallId)) {
    return (
      <Suspense fallback={null}>
        <RiskExplanationCard toolCallId={tool.toolCallId} />
      </Suspense>
    );
  }
  if (tool.toolName === "prepare_decision_chain" && hasDecisionChain(tool.toolCallId)) {
    return (
      <Suspense fallback={null}>
        <DecisionChainCard toolCallId={tool.toolCallId} />
      </Suspense>
    );
  }
  if (tool.toolName === "prepare_cash_flow" && hasCashFlow(tool.toolCallId)) {
    return (
      <Suspense fallback={null}>
        <CashFlowCard toolCallId={tool.toolCallId} />
      </Suspense>
    );
  }
  if (tool.toolName === "prepare_annual_followup" && hasAnnualFollowup(tool.toolCallId)) {
    return (
      <Suspense fallback={null}>
        <AnnualFollowupCard toolCallId={tool.toolCallId} />
      </Suspense>
    );
  }
  return null;
}

const ChatPrepareWidgets = lazy(() =>
  import("@/features/advice-portal/components/chat-prepare/ChatPrepareWidgets").then((module) => ({
    default: module.ChatPrepareWidgets,
  })),
);

const MeetingBriefCard = lazy(() =>
  import("@/features/clients/components/meeting-brief/MeetingBriefCard").then((module) => ({
    default: module.MeetingBriefCard,
  })),
);

const OrderBatchCard = lazy(() =>
  import("@/features/order-instructions/components/order-batch/OrderBatchCard").then((module) => ({
    default: module.OrderBatchCard,
  })),
);

const ChatProposalCard = lazy(() =>
  import("@/features/portfolio-analysis/components/chat-proposal/ChatProposalCard").then(
    (module) => ({ default: module.ChatProposalCard }),
  ),
);

const ProposalMailCard = lazy(() =>
  import("@/features/portfolio-analysis/components/chat-proposal/ProposalMailCard").then(
    (module) => ({ default: module.ProposalMailCard }),
  ),
);

const RiskExplanationCard = lazy(() =>
  import("@/features/portfolio-analysis/components/chat-proposal/RiskExplanationCard").then(
    (module) => ({ default: module.RiskExplanationCard }),
  ),
);

const DecisionChainCard = lazy(() =>
  import("@/features/documents/components/decision-chain/DecisionChainCard").then((module) => ({
    default: module.DecisionChainCard,
  })),
);

const CashFlowCard = lazy(() =>
  import("@/features/portfolio-analysis/components/chat-proposal/CashFlowCard").then((module) => ({
    default: module.CashFlowCard,
  })),
);

const AnnualFollowupCard = lazy(() =>
  import("@/features/clients/components/annual-followup/AnnualFollowupCard").then((module) => ({
    default: module.AnnualFollowupCard,
  })),
);
